import { SagaIterator } from '@redux-saga/core';
import { IMPERSONATION_ERROR_CODE } from '~/components/_common/constants';
import { call, put, takeLatest, takeLeading, select, delay } from 'redux-saga/effects';

import { FEATURE_FLAG } from '~/constants/feature-flags';
import api from '~/store/api';
import { creators as notificationCreators } from '~/store/notification';
import { selectFeatures } from '~/store/session';
import { getCurrentDate } from '~/utils/misc';

import { GetPatientTrackerTabCountsRequestBody } from '../api/types';
import { creators } from './actions';
import { tabNamesForPage } from './constants';
import { getFiltersForReq, transformTabCounts, withMatchReviewProgramTypes } from './helpers';
import {
  GET_PATIENT_TRACKER_V3_RECORDS,
  GET_PATIENT_TRACKER_V3_RECORDS_CSV,
  GetPatientTrackerRecords,
  GetPatientTrackerRecordsCsv,
  GET_PATIENT_TRACKER_V3_RECORDS_FAILED,
  GET_PATIENT_TRACKER_V3_CATEGORY_COUNTS,
  GetPatientCategoryCounts,
  SHOW_PATIENT_TRACKER_V3_ERROR_MESSAGE,
  GET_PATIENT_TRACKER_V3_RECORDS_CSV_FAILED,
} from '../api/types/patientTrackerStore';

// Track the latest request ID for each stateMapKey to prevent stale responses
const latestCategoryCountsRequestId: Record<string, number> = {};
const latestRecordsRequestId: Record<string, number> = {};

const siteTrialsFeatureEnabled = (features: string[] | undefined) =>
  Boolean(features?.includes(FEATURE_FLAG.SITE_TRIALS));

function* getPatientTrackerRecords({ id: stateMapKey, siteId }: GetPatientTrackerRecords): SagaIterator {
  // Generate a unique request ID to track this request
  const requestId = Date.now();
  latestRecordsRequestId[stateMapKey] = requestId;

  // To debounce network requests (takeLatest) to deal with the latest requests not completing
  // due to some logic in the axios caching in t-shared interfering with takeLatest
  // Works without this line if disabling caching when setting up axios
  // or when sending in a dummy parameter with request to force separate request
  // Need to dig deeper to find exact issue
  yield delay(300);

  // Check if this request is still the latest one after the delay
  if (latestRecordsRequestId[stateMapKey] !== requestId) {
    return; // A newer request has been made, discard this one
  }

  try {
    const { tab, filters, patientsMetadata } = yield select(({ patientTrackerV3 }) => patientTrackerV3[stateMapKey]);
    const features: string[] | undefined = yield select(selectFeatures);
    const { sortBy, filtersForReq } = getFiltersForReq(siteId, filters, tab);
    const requestFilters = withMatchReviewProgramTypes(
      stateMapKey,
      filters,
      filtersForReq,
      siteTrialsFeatureEnabled(features),
    );

    const req = {
      filters: requestFilters,
      sortBy,
      page: patientsMetadata.skip + 1,
      pageSize: patientsMetadata.numRecords,
    };

    const { patients, patientCount } = yield call(api.patientCohortMatch.getQueue, req);

    // Check again after the API call to ensure this is still the latest request
    if (latestRecordsRequestId[stateMapKey] !== requestId) {
      return; // A newer request has been made, discard this one
    }

    yield put(creators.setPatientTrackerRecords(stateMapKey, patients ?? []));
    yield put(
      creators.setPatientTrackerPagination(
        stateMapKey,
        patientsMetadata.numRecords,
        patientsMetadata.skip,
        patientCount,
      ),
    );
  } catch (error) {
    // Only handle error if this is still the latest request
    if (latestRecordsRequestId[stateMapKey] === requestId) {
      yield put(creators.getPatientTrackerRecordsFailed(stateMapKey, error));
    }
  }
}

function* getPatientRecordsCsv({ id, siteId }: GetPatientTrackerRecordsCsv): SagaIterator {
  try {
    const { tab, filters } = yield select(({ patientTrackerV3 }) => patientTrackerV3[id]);
    const features: string[] | undefined = yield select(selectFeatures);
    const { filtersForReq, sortBy } = getFiltersForReq(siteId, filters, tab);

    const data = yield call(api.patientCohortMatch.getQueueCsv, {
      filters: withMatchReviewProgramTypes(id, filters, filtersForReq, siteTrialsFeatureEnabled(features)),
      sortBy,
    });

    const currentDate = getCurrentDate().replace(/\//g, '-');
    const fileName = `TIME_patient_tracker_${siteId}_${currentDate}_${Date.now()}.csv`;
    yield put(creators.setPatientTrackerRecordsCsv(id, fileName, data));
  } catch (error) {
    yield put(creators.getPatientTrackerRecordsCsvFailed(id, error));
  }
}

function* getPatientTrackerCategoryCounts({ id: stateMapKey, siteId }: GetPatientCategoryCounts): SagaIterator {
  // Generate a unique request ID to track this request
  const requestId = Date.now();
  latestCategoryCountsRequestId[stateMapKey] = requestId;

  // To debounce network requests (takeLatest) to deal with the latest requests not completing
  // due to some logic in the axios caching in t-shared interfering with takeLatest
  // Works without this line if disabling caching when setting up axios
  // or when sending in a dummy parameter with request to force separate request
  // Need to dig deeper to find exact issue
  yield delay(300);

  // Check if this request is still the latest one after the delay
  if (latestCategoryCountsRequestId[stateMapKey] !== requestId) {
    return; // A newer request has been made, discard this one
  }

  try {
    const { filters } = yield select(({ patientTrackerV3 }) => patientTrackerV3[stateMapKey]);
    const features: string[] | undefined = yield select(selectFeatures);

    const { filtersForReq } = getFiltersForReq(siteId, filters);

    const counts = yield call(api.patientCohortMatch.getQueueTabCounts, {
      filters: withMatchReviewProgramTypes(stateMapKey, filters, filtersForReq, siteTrialsFeatureEnabled(features)),
      tabNames: tabNamesForPage[stateMapKey],
    } as GetPatientTrackerTabCountsRequestBody);

    // Check again after the API call to ensure this is still the latest request
    if (latestCategoryCountsRequestId[stateMapKey] !== requestId) {
      return; // A newer request has been made, discard this one
    }

    yield put(creators.setPatientTrackerCategoryCounts(stateMapKey, transformTabCounts(counts)));
  } catch (error) {
    // Only handle error if this is still the latest request
    if (latestCategoryCountsRequestId[stateMapKey] === requestId) {
      yield put(creators.showPatientTrackerErrorMessage(stateMapKey, error, 'Failed to fetch category counts'));
    }
  }
}

function* putErrorMessage(action): SagaIterator {
  const { error, defaultErrorMessage } = action;
  const { name, response, message } = error;
  const { statusText, data, status } = response || {};
  let errorText = message;

  if (name === IMPERSONATION_ERROR_CODE) {
    errorText = message;
  } else if (status === 409) {
    errorText = data.message;
  } else if (defaultErrorMessage) {
    errorText = defaultErrorMessage;
  } else if (statusText) {
    errorText = `${statusText}: ${data.message || message}`;
  }

  yield put(notificationCreators.showErrorMessage(errorText));
}

export default [
  takeLatest(GET_PATIENT_TRACKER_V3_RECORDS, getPatientTrackerRecords),
  takeLeading(GET_PATIENT_TRACKER_V3_RECORDS_CSV, getPatientRecordsCsv),
  takeLatest(GET_PATIENT_TRACKER_V3_RECORDS_FAILED, putErrorMessage),
  takeLatest(GET_PATIENT_TRACKER_V3_RECORDS_CSV_FAILED, putErrorMessage),
  takeLatest(GET_PATIENT_TRACKER_V3_CATEGORY_COUNTS, getPatientTrackerCategoryCounts),
  takeLatest(SHOW_PATIENT_TRACKER_V3_ERROR_MESSAGE, putErrorMessage),
];
